import React from 'react';
import { Toggle, Segmented, Row, Section, NumberInputWithUnit } from '../settingsPrimitives';
import { usePreferencesStore } from '../../store/preferencesStore';
import SslVerificationSetting from './SslVerificationSetting';

/**
 * Settings → Requests.
 *
 * Until 24.09.2026 every control on this page was saved and never read: the
 * timeout, redirect and HTTP-version preferences did not reach the sender, and
 * this page's own "SSL certificate verification" switch contradicted the one
 * on Settings → Certificates, which was the only one the sender obeyed.
 *
 *   * Timeout, redirects and HTTP version reach `send_http_request` through
 *     `utils/requestNetworkOptions.js` on every request the user sends.
 *   * SSL verification has ONE source of truth: the certificate store's
 *     `verifySSL`. This switch is a second view of that same value, so the two
 *     screens cannot disagree (SslVerificationSetting, shared with the
 *     Certificates page: same wording, same polarity, same warning). The old `sslVerification` preference is no
 *     longer read or written (it never had an effect).
 *   * In the browser build the browser sends the request and decides all of
 *     this itself, so the controls are disabled and say so rather than sitting
 *     there switched on, doing nothing.
 */
const RequestsSettings = ({ t, desktopApp }) => {
  const requestTimeout = usePreferencesStore((s) => s.requestTimeout);
  const followRedirects = usePreferencesStore((s) => s.followRedirects);
  const httpVersion = usePreferencesStore((s) => s.httpVersion);
  const update = usePreferencesStore((s) => s.updatePreference);
  const disabled = !desktopApp;
  const why = (desc) => (disabled ? t('settings.requestsDesktopOnly') : desc);

  return (
    <Section title={t('settings.network')}>
      <Row label={t('settings.requestTimeout')} desc={why(t('settings.requestTimeoutDesc'))}>
        <NumberInputWithUnit
          value={requestTimeout}
          onChange={(v) => update('requestTimeout', v)}
          min={0}
          max={300000}
          unit="ms"
          width={72}
          disabled={disabled}
        />
      </Row>
      <Row label={t('settings.followRedirects')} desc={why(t('settings.followRedirectsDesc'))}>
        <Toggle
          value={followRedirects}
          onChange={(v) => update('followRedirects', v)}
          disabled={disabled}
        />
      </Row>
      <SslVerificationSetting t={t} disabled={disabled} desc={why(t('settings.sslVerificationDesc'))} />
      <Row label={t('settings.httpVersion')} desc={why(t('settings.httpVersionDesc'))}>
        <Segmented
          value={httpVersion}
          onChange={(v) => update('httpVersion', v)}
          disabled={disabled}
          options={[
            { id: 'auto', label: t('settings.httpVersionAuto') },
            { id: '1.1', label: t('settings.httpVersion11') },
            { id: '2', label: t('settings.httpVersion2') },
          ]}
        />
      </Row>
    </Section>
  );
};

export default RequestsSettings;
